/**
 * Sprint V96 · Fokustyp Video-Bridge (Mi 16.09.2026, Todd Dickerson / Andreas).
 * Sprint V96.1 (Update, Feb 2026): Technische Angleichung an das
 * Dashboard-Vimeo-Muster (`/app/frontend/src/components/VimeoEmbed.jsx`):
 *   - Statisches <iframe> mit iOS-safen Params
 *     (playsinline=1, dnt=1, title=0, byline=0, portrait=0, h=<hash>)
 *   - DSGVO-Consent-Gate vor Load (Vimeo-Cookies)
 *   - Player.js NACHTRAEGLICH ans iframe haengen fuer VideoPlay/VideoComplete
 *   - Selbes `allow`-Attribut (autoplay; fullscreen; picture-in-picture;
 *     encrypted-media; clipboard-write) + strict-origin referrer policy
 *
 * Ablauf:
 *   1. Mount: fetch `/api/video-bridge/config?fokustyp=<slug>` → {video_id, video_hash, has_key}
 *   2. Wenn has_key: Consent-Gate → nach Consent: statisches iframe rendern.
 *   3. Sonst: Fallback-Bild + CTA bleibt sichtbar.
 *   4. Bridge-Events (result_shown, video_started, cta_clicked) an
 *      `/api/tracking/video-bridge` (fire-and-forget).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { PlayCircle, VideoOff, Play } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  trackVideoBridgeShown,
  trackVideoBridgePlay,
  trackVideoBridgeProgress,
  trackVideoBridgeComplete,
  trackVideoBridgeCtaClick,
} from "@/lib/ga4";
import { useJourneyFixes } from "@/lib/journeyFlags";

const VALID = new Set(["katze", "eule", "biene", "hund"]);
const PLAYER_SCRIPT_SRC = "https://player.vimeo.com/api/player.js";

function readOrCreateSessionId() {
  try {
    const existing = localStorage.getItem("f4s_session_id");
    if (existing && existing.length >= 12) return existing;
    const fresh = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem("f4s_session_id", fresh);
    return fresh;
  } catch {
    return `s_ephemeral_${Date.now().toString(36)}`;
  }
}

function track(event, fokustyp) {
  try {
    const body = {
      event,
      fokustyp,
      session_id: readOrCreateSessionId(),
    };
    fetch(`${api.defaults.baseURL}/tracking/video-bridge`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(window.__f4s_testMode ? { "X-F4S-Test-Run": "1" } : {}) },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* silent */
  }
}

function ensurePlayerScript() {
  return new Promise((resolve) => {
    if (typeof window === "undefined") return resolve();
    if (window.Vimeo && window.Vimeo.Player) return resolve();
    const existing = document.querySelector(`script[src="${PLAYER_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      return;
    }
    const s = document.createElement("script");
    s.src = PLAYER_SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve();
    document.body.appendChild(s);
  });
}

/** Baut die Player-URL — identisch zu VimeoEmbed.jsx (iOS-safe). */
function buildPlayerUrl(videoId, videoHash) {
  const params = new URLSearchParams({
    playsinline: "1",
    dnt: "1",
    title: "0",
    byline: "0",
    portrait: "0",
  });
  if (videoHash) params.set("h", videoHash);
  return `https://player.vimeo.com/video/${videoId}?${params.toString()}`;
}

export default function VideoBridge({ fokustyp, onCta, ctaLabel, hideCtaForUser }) {
  const { user } = useAuth();
  const fixes = useJourneyFixes() === true;
  const [config, setConfig] = useState(null);
  const [consented, setConsented] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const startedRef = useRef(false);
  const shownRef = useRef(false);
  // 02.10.2026 — Progress-Milestones (25/50/75 %) + complete. Pro Mount
  // + Milestone genau einmal, damit Scrubbing / Re-Plays nicht doppelt
  // feuern. Wir nutzen Set statt booleans für kompaktere Logik.
  const progressSentRef = useRef(new Set());

  const validFokustyp = VALID.has(fokustyp) ? fokustyp : null;

  // Config laden
  useEffect(() => {
    if (!validFokustyp) { setConfig(null); return; }
    let cancelled = false;
    api
      .get(`/video-bridge/config?fokustyp=${encodeURIComponent(validFokustyp)}`)
      .then((r) => { if (!cancelled) setConfig(r.data); })
      .catch(() => { if (!cancelled) setConfig({ has_key: false }); });
    return () => { cancelled = true; };
  }, [validFokustyp]);

  // Event: result_shown (einmal pro Mount + Fokustyp)
  useEffect(() => {
    if (!validFokustyp || shownRef.current) return;
    shownRef.current = true;
    track("result_shown", validFokustyp);
    // Sprint V96.3 — GA4 mirror
    trackVideoBridgeShown({ fokustyp: validFokustyp, hasVideo: Boolean(config?.has_key) });
  }, [validFokustyp, config?.has_key]);

  const hasVideo = Boolean(config?.has_key) && !loadError;
  const videoId = config?.video_id || null;
  const videoHash = config?.video_hash || null;
  const playerUrl = useMemo(
    () => (videoId ? buildPlayerUrl(videoId, videoHash) : null),
    [videoId, videoHash]
  );

  // Player.js an iframe haengen — nur fuer Tracking. Analog VimeoEmbed.jsx.
  useEffect(() => {
    if (!hasVideo || !consented || !videoId || !iframeRef.current) return;
    let destroyed = false;
    (async () => {
      await ensurePlayerScript();
      if (destroyed || !iframeRef.current || playerRef.current) return;
      try {
        playerRef.current = new window.Vimeo.Player(iframeRef.current);
        playerRef.current.on("play", () => {
          if (startedRef.current) return;
          startedRef.current = true;
          track("video_started", validFokustyp);
          // Sprint V96.3 — GA4 `video_start` (Recommended-Event)
          trackVideoBridgePlay({ fokustyp: validFokustyp, videoId });
          // Meta Pixel Custom-Event (falls Consent bereits erteilt)
          try {
            if (typeof window.fbq === "function") {
              window.fbq("trackCustom", "VideoBridgePlay", {
                video_id: videoId,
                fokustyp: validFokustyp,
              });
            }
          } catch { /* silent */ }
        });
        // 02.10.2026 — Progress-Milestones (25/50/75 %). Vimeo liefert
        // `timeupdate` ~4x/s mit {percent: 0..1, seconds, duration}.
        // Wir feuern pro Milestone genau einmal (Set).
        playerRef.current.on("timeupdate", (data) => {
          const pct = Math.floor((data?.percent || 0) * 100);
          for (const milestone of [25, 50, 75]) {
            if (pct >= milestone && !progressSentRef.current.has(milestone)) {
              progressSentRef.current.add(milestone);
              const eventName = `video_progress_${milestone}`;
              track(eventName, validFokustyp);
              trackVideoBridgeProgress({
                fokustyp: validFokustyp,
                videoId,
                percent: milestone,
              });
            }
          }
        });
        playerRef.current.on("ended", () => {
          // 02.10.2026 — video_complete auch an Backend-Tracking schicken
          track("video_complete", validFokustyp);
          // Sprint V96.3 — GA4 `video_complete` (Recommended-Event)
          trackVideoBridgeComplete({ fokustyp: validFokustyp, videoId });
          try {
            if (typeof window.fbq === "function") {
              window.fbq("trackCustom", "VideoBridgeComplete", {
                video_id: videoId,
                fokustyp: validFokustyp,
              });
            }
          } catch { /* silent */ }
        });
      } catch (err) {
        console.warn("VideoBridge player attach failed", err);
      }
    })();
    return () => {
      destroyed = true;
      try { playerRef.current?.destroy(); } catch { /* noop */ }
      playerRef.current = null;
    };
  }, [hasVideo, consented, videoId, validFokustyp]);

  const handleCta = () => {
    track("cta_clicked", validFokustyp);
    // Sprint V96.3 — GA4 mirror
    trackVideoBridgeCtaClick({ fokustyp: validFokustyp });
    if (typeof onCta === "function") onCta();
  };

  if (!validFokustyp) return null;

  return (
    <div
      className="mt-8 flex flex-col items-center"
      data-testid="video-bridge"
      data-fokustyp={validFokustyp}
      data-has-video={hasVideo ? "true" : "false"}
    >
      <div className="w-full max-w-2xl rounded-2xl overflow-hidden shadow-lg border border-slate-200 dark:border-slate-700 bg-slate-900">
        {config === null && fixes ? (
          <div className="w-full aspect-video flex items-center justify-center bg-slate-900" data-testid="video-bridge-loading">
            <div className="w-8 h-8 rounded-full border-2 border-slate-600 border-t-slate-200 animate-spin" />
          </div>
        ) : hasVideo ? (
          consented ? (
            // ============ LOADED ============
            <div className="relative w-full" style={{ paddingBottom: "56.25%" /* 16:9 */ }}>
              <iframe
                ref={iframeRef}
                src={playerUrl}
                title={`Video-Bridge Fokustyp ${validFokustyp}`}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; encrypted-media; clipboard-write"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                onError={() => setLoadError(true)}
                data-testid="video-bridge-iframe"
              />
            </div>
          ) : (
            // ============ AWAITING CONSENT ============
            <div
              className="w-full aspect-video flex flex-col items-center justify-center text-center p-6 bg-black/80 text-white"
              data-testid="video-bridge-consent"
            >
              <h3 className="font-bold text-lg sm:text-xl mb-2">
                Dein persönliches Fokustyp-Video
              </h3>
              <p className="text-white/70 text-sm max-w-sm mb-5">
                Wir zeigen dieses Video über Vimeo. Beim Aktivieren lädt Vimeo
                Cookies auf deinem Gerät.
              </p>
              <button
                type="button"
                onClick={() => setConsented(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#4563BB] hover:bg-[#3a54a3] text-white font-semibold shadow-sm transition-colors"
                data-testid="video-bridge-consent-btn"
              >
                <Play className="w-4 h-4" fill="currentColor" />
                Video laden
              </button>
            </div>
          )
        ) : (
          // ============ FALLBACK ============
          <div
            className="w-full aspect-video flex flex-col items-center justify-center gap-3 text-slate-100 bg-gradient-to-br from-slate-900 to-slate-800 p-8"
            data-testid="video-bridge-fallback"
          >
            <VideoOff className="w-12 h-12 text-slate-400" aria-hidden="true" />
            <p className="text-sm text-slate-300 text-center max-w-md">
              Dein persönliches Willkommens-Video wird gerade veröffentlicht.
              {(!user || !(fixes || hideCtaForUser)) && " Du kannst schon jetzt deinen Fokustyp-Zugang aktivieren."}
            </p>
          </div>
        )}
      </div>

      {/* CTA — Wortlaut per Todd/Andreas-Spec exakt. Eingeloggt ohne eigenen CTA: kein Zugang-Button. */}
      {(onCta || !user || !(fixes || hideCtaForUser)) && <>
      <button
        type="button"
        onClick={handleCta}
        data-testid="video-bridge-cta"
        className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[var(--fts-accent)] hover:opacity-90 text-white font-bold px-8 py-4 text-base sm:text-lg transition-opacity shadow-lg"
      >
        <PlayCircle className="w-5 h-5" aria-hidden="true" />
        {ctaLabel || "Persönlichen Zugang erhalten"}
      </button>
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
        Ein Klick per E-Mail &mdash; kein Passwort.
      </p>
      </>}
    </div>
  );
}
